import { Button, Form, TabPanel } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import DataSource from 'devextreme/data/data_source';
import { formatDate } from 'devextreme/localization';
import moment from 'moment';
import { equals } from 'ramda';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { putApi } from '../../apiUtils';
import { Loader } from '../../components/loader/Loader';
import { useAssetClass } from '../../contexts/assetClass';
import { useTranslation } from '../../contexts/translation';
import { calculateAttachement } from '../../functions/counters';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useSafeGoBack } from '../../hooks/useSafeGoBack';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import { swrConfig } from '../../pages/Assets/AssetsEdit';
import { timeSpanToHoursMinutes } from '../../pages/Tasks/Columns';
import { TaskServiceComments } from '../../pages/Tasks/ServiceDesk/Comments/TaskServiceComments';
import { RelatedAsset } from '../../pages/Tasks/ServiceDesk/Update/RelatedAsset/RelatedAsset';
import { tabsIndexes } from '../../pages/Tasks/ServiceDesk/Update/TaskServiceDeskUpdate';
import { TaskFilesTab } from '../../pages/Tasks/Tabs/TaskFilesTab';
import { TaskHistoryTab } from '../../pages/Tasks/Tabs/TaskHistoryTab';
import { TaskWorkSheetsTab } from '../../pages/Tasks/Tabs/TaskWorkSheetsTab';
import { TaskChangeStatus } from '../../pages/Tasks/TaskChangeStatus';
import { Asset } from '../../types/asset.types';
import { CapacityCategory } from '../../types/capacityCategory.types';
import { Customer } from '../../types/customer.types';
import {
  CustomerProject,
  CustomerProjectBillingStatus,
  CustomerProjectBillingStatusTranslation,
} from '../../types/customerProject.types';
import { DocumentType } from '../../types/documentType.types';
import { FaultType } from '../../types/faultType';
import { LocationState } from '../../types/locationState.types';
import { Person } from '../../types/person.types';
import { Task } from '../../types/task.types';
import { TaskRatings } from '../../types/taskRating.types';
import { TaskStatusApiResponse } from '../../types/TaskStatus';
import { getColorClass, getUserColorClass, UserColor } from '../../types/userColor.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import {
  dateFieldOptionsWithSerialization,
  dateTimeFieldOptions,
  dateTimeFieldOptionsWithSerialization,
} from '../../utils/dates.types';
import { STORAGE_KEYS } from '../../utils/localStorageKeys';
import { dxSelectBoxConfig } from './TasksForm';
import './TasksServiceDesk.scss';

interface UpdateFormData {
  id: number;
  taskName: string;
  description: string;
  diagnose: string;

  responsiblePersonId: number | null;
  customerId: number | null;
  customerProjectId: number | null;
  typeId: number | null;
  topLevelAssetId: number | null;
  assetId: number | null;
  priorityId: number | null;
  capacityCategoryId: number;
  faultTypeId: number | null;
  requiredDate: Date | null | string;
  downtimeFrom: Date | null | string;
  downtimeTo: Date | null | string;
  creationTime: Date | null | string;

  requestorName?: string;
  requestorEmail?: string;
  requestorPhone?: string;
  statusNameTranslated?: string;
  billingStatus: string | null;
}

interface Props {
  task: Task;
  comments: any;
  defaultValues: UpdateFormData;
  onActionDone: () => void;
}

export const TasksServiceDeskUpdateForm = ({
  task,
  comments,
  defaultValues,
  onActionDone,
}: Props) => {
  const { assetClass } = useAssetClass();
  const { translate } = useTranslation();
  const history = useHistory();
  const transFormName = 'Task.UpdateServiceDesk';

  // Back router
  const { state }: { state: LocationState | undefined } = useLocation();
  const [contextState] = useLocalStorage(STORAGE_KEYS.CONTEXT_STATE, {});
  const backRoute = state?.backRoute
    ? state.backRoute
    : (contextState && contextState.backRoute) ?? '';
  const safeGoBack = useSafeGoBack('/home');
  const [prompt, setDirty, setClean, handleCloseButton, isDirty, handleDirtyWithCallback] =
    useUnsavedChangesWarning(backRoute, (backRouteObject) => {
      if (!formRef) return;
      if (formRef.current!.instance.validate().isValid)
        onSubmit(null).then((result) => {
          if (result)
            if (!backRouteObject) safeGoBack();
            else history.push(backRouteObject);
          // history.push({
          //   pathname: backRoute,
          //   state: { loadSettingFromSession: true },
          // });
          else setDirty();
        });
    });

  // States
  const [localComments, setLocalComments] = useState<any[]>(comments);
  const [tabs, setTabs] = useState<any[]>([]);
  const [currentTab, setCurrentTab] = useState<number>(
    state && state.defaultTabIndex ? state.defaultTabIndex - 1 : tabsIndexes.COMMENTS,
  );

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    defaultValues.customerId,
  );
  const [selectedTopLevelAssetId, setSelectedTopLevelAssetId] = useState<number | null>(
    defaultValues.topLevelAssetId,
  );
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [showTaskNameInput, setShowTaskNameInput] = useState(false);
  const [userColorClass, setUserColorClass] = useState<string | null>(null);
  const [projectColorClass, setProjectColorClass] = useState<string | null>();
  // Show inputs
  const [isShowDowntime, setShowDowntime] = useState<boolean>(false);
  const [isShowFaultType, setShowFaultType] = useState<boolean>(false);

  const formRef = useRef<any>();
  const formData = useRef<UpdateFormData>(defaultValues);

  // Načte zodpovědné osoby pro task action. Pouze v editaci.
  const { data: taskActionRespPersons, isValidating: validatingTaskActionRespPersons } = useSWR<
    Person[]
  >(
    formData && formData.current.capacityCategoryId
      ? `${API_URL.CAPACITY_CATEGORY_PERSON_LIST}?parentId=${formData.current.capacityCategoryId}`
      : null,
    fetcher,
    swrConfig,
  );
  // Načteme stavy pro akční tlačítka.
  const { data: statuses, mutate: mutateStatuses } = useSWR<TaskStatusApiResponse>(
    `${API_URL.TASK_PROCESS_CONFIG}?taskId=${task.id}`,
    fetcher,
    swrConfig,
  );
  // Načtení rating ikon
  const { data: taskRatings } = useSWR<TaskRatings>(
    `${API_URL.TASK_RATING_LIST}?visibleOnly=true`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: (result) => {
        let sortedArr = result.sort((a: any, b: any) =>
          a.rating > b.rating ? 1 : b.rating > a.rating ? -1 : 0,
        );
        return sortedArr;
      },
    },
  );

  const { data: customers } = useSWR<Customer[]>(`${API_URL.CUSTOMER_LIST}`, fetcher, swrConfig);
  const { data: projects } = useSWR<CustomerProject[]>(
    selectedCustomerId ? `${API_URL.CUSTOMER_PROJECT_LIST}?parentId=${selectedCustomerId}` : null,
    fetcher,
    swrConfig,
  );
  const { data: documentTypes } = useSWR<DocumentType[]>(
    `${API_URL.GET_LIST_FOR_TASK_CREATE}?parentId=${assetClass}&taskCreateDesktop=true&visibleOnly=true&validateForUser=true`,
    fetcher,
    swrConfig,
  );
  const { data: capacityCategories } = useSWR<CapacityCategory[]>(
    `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}`,
    fetcher,
    swrConfig,
  );
  const { data: topLevelAssets } = useSWR<Asset[]>(
    `${API_URL.ASSET_LIST_BY_CLASS_TOP_LEVEL}?classId=${assetClass}&activeOnly=true&visibleOnly=true`,
    fetcher,
    swrConfig,
  );
  const { data: assets } = useSWR<Asset[]>(
    selectedTopLevelAssetId
      ? `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}&parentId=${selectedTopLevelAssetId}&assetToInclude=${selectedTopLevelAssetId}`
      : `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}`,
    fetcher,
    swrConfig,
  );
  const { data: assetsAssignToCustomer } = useSWR<Asset[]>(
    selectedCustomerId
      ? `${API_URL.ASSET_LIST_FOR_CUSTOMER}?customerId=${selectedCustomerId}`
      : null,
    fetcher,
    swrConfig,
  );

  const { data: responsiblePersons } = useSWR<Person[]>(
    API_URL.PERSON_LIST_WITH_CAPACITY_CAT,
    fetcher,
  );
  const { data: faultTypes } = useSWR<FaultType[]>(API_URL.FAULT_TYPE_LIST, fetcher);
  const { data: attachmentsCount, mutate: mutateAttachmentCount } = useSWR(
    `${API_URL.FILE_COUNT}?entity=Task&entityId=${task.id}`,
    fetcher,
    swrConfig,
  );
  const { data: filesRelativesCount, mutate: mutateAttachmentRelativesCount } = useSWR<number>(
    task ? `${API_URL.FILE_COUNT}?entity=Asset&entityId=${task.assetId}` : null,
    fetcher,
  );

  const { data: userColors } = useSWR<UserColor[]>(
    `${API_URL.USER_COLOR_LIST}`,
    fetcher,
    swrConfig,
  );

  const { data: hyperlinksCount, mutate: mutateHyperlinksCount } = useSWR<number>(
    `${API_URL.HYPERLINK_COUNT}?entity=Task&entityId=${task.id}`,
    fetcher,
    swrConfig,
  );
  const { data: hyperlinksRelativesCount, mutate: mutateHyperlinksRelativesCount } = useSWR<number>(
    task ? `${API_URL.HYPERLINK_COUNT}?entity=Asset&entityId=${task.assetId}` : null,
    fetcher,
  );

  const { data: taskWorksheetList, error: errorTaskWorksheetList } = useSWR(
    `${API_URL.WORKSHEET_LIST}?parentId=${task.id}`,
    fetcher,
    swrConfig,
  );
  const { data: worksheetsTotalElapsedTime } = useSWR(
    `${API_URL.WORKSHEET_TOTAL_ELAPSED_TIME}?taskId=${task.id}`,
    fetcher,
    swrConfig,
  );

  // Při změně assetu se vybere parent
  const handleAssetChanged = useCallback(
    (assetId: number) => {
      if (selectedTopLevelAssetId || !assets) return;
      const asset: Asset = assets.filter((x) => x.id === assetId)[0];
      if (!asset) return;
      formRef.current.instance.updateData('topLevelAssetId', asset.topLevelParenId);
    },
    [selectedTopLevelAssetId, assets],
  );

  // Při zěmně projektu se předvyplní billing status
  const handleCustomerProjectChanged = useCallback(
    (customerProjectId: number) => {
      if (!projects || !customerProjectId) return;
      const project = projects.filter((x) => x.id === customerProjectId)[0];
      formRef.current.instance.updateData('billingStatus', project.defaultBillingStatus);
    },
    [projects],
  );

  /**
   * Pokud se jedná o editační formulář, potřebujeme již při načtení formuláře nastavit viditelnost
   * prvků v závislosti na documentType.
   */
  useEffect(() => {
    if (!documentTypes || !formData || !formData.current) return;

    const documentType = documentTypes.find((x) => x.id === formData.current.typeId);
    if (!documentType) return;

    setShowDowntime(documentType ? documentType.showDowntime : false);
    setShowFaultType(documentType ? documentType.taskShowFaultType : false);
  }, [task, formData]);

  useEffect(() => {
    if (!projects || !defaultValues.customerProjectId) return;
    const color = projects.find((x) => x.id === defaultValues.customerProjectId);
    if (color) {
      // @ts-ignore
      setProjectColorClass(getColorClass(color.color));
    }
  }, [defaultValues.customerProjectId, projects]);

  /**
   * Změna DocumentType. Na základě documentType je třeba zobrazit/skrýt (obecně upravit) prvky formuláře.
   * @param data:  Data z dropDownListu (DocumentTypeId)
   */
  const handleDocumentTypeChanged = (data) => {
    if (!documentTypes) return;

    const documentType = documentTypes.find((x) => x.id === data.value);
    if (!documentType) return;

    setShowDowntime(documentType ? documentType.showDowntime : false);
    setShowFaultType(documentType ? documentType.taskShowFaultType : false);
  };

  /**
   * Validační funkce, která se volá při provedení TaskAction
   * Funkce se pokusí provést Edit, pokud se nepodaří, TaskAction se nevykoná
   */
  const customValidationFunctionActions = async (): Promise<boolean> => {
    if (!isDirty) return true;

    if (!formRef || !formRef.current!.instance.validate().isValid) return false;

    const result = await onSubmit(null).then((result) => {
      return result;
    });

    if (!result) {
      setDirty();
      return false;
    }

    return true;
  };

  const onSubmit = async (e) => {
    if (e !== null) e.preventDefault();
    if (!formData) return;
    let isOk = false;

    setClean();
    isOk = await submitPut();
    if (isOk) {
      onActionDone();
    }

    return isOk;
  };

  const submitPut = useCallback(async () => {
    let isOk = false;
    await putApi({
      url: API_URL.TASK_PUT,
      data: formData.current,
      callAfterSuccess: async (data) => {
        isOk = true;
      },
    });
    return isOk;
  }, []);

  useEffect(() => {
    if (!task) return;
    setTabs([
      {
        title: `${translate!('Comments', transFormName)} (${task?.comments.length})`,
        index: tabsIndexes.COMMENTS,
        component: equals(currentTab, 0) && (
          <TaskServiceComments
            task={task}
            comments={localComments}
            active={equals(0, currentTab)}
            onAdd={(comment: any) => {
              setLocalComments([comment, ...localComments]);
            }}
            onUpdate={(comment: any) => {
              setLocalComments([
                ...localComments.map((item) => (item.id === comment.id ? comment : item)),
              ]);
            }}
            onDelete={(commentId: number) => {
              setLocalComments([...localComments.filter((x) => x.id !== commentId)]);
            }}
            enableEdit
            enableDelete
          />
        ),
      },
      {
        title: `${translate!('Files', transFormName)}  (${calculateAttachement(
          attachmentsCount,
          hyperlinksCount,
          filesRelativesCount,
          hyperlinksRelativesCount,
        )})`,
        index: tabsIndexes.FILES,
        component: equals(currentTab, 1) && (
          <TaskFilesTab
            id={task.id.toString()}
            assetId={task.assetId}
            assetName={task.assetName}
            active={equals(1, currentTab)}
            mutateAttachmentCount={() => {
              mutateAttachmentCount();
              mutateAttachmentRelativesCount();
              mutateHyperlinksCount();
              mutateHyperlinksRelativesCount();
            }}
          />
        ),
      },
      {
        title: translate!('History', transFormName),
        index: tabsIndexes.HISTORY,
        component: equals(currentTab, 2) && (
          <TaskHistoryTab id={task.id.toString()} active={equals(2, currentTab)} />
        ),
      },
      {
        title: `${translate!('Worksheets', transFormName)} (${taskWorksheetList?.length})`,
        index: tabsIndexes.WORKSHEETS,
        component: equals(currentTab, 3) && (
          <TaskWorkSheetsTab
            task={task}
            id={''}
            active={equals(3, currentTab)}
            data={taskWorksheetList}
            error={errorTaskWorksheetList}
          />
        ),
      },
    ]);
  }, [
    task,
    currentTab,
    localComments,
    attachmentsCount,
    hyperlinksCount,
    taskWorksheetList,
    errorTaskWorksheetList,
  ]);

  useEffect(() => {
    if (!userColors || !defaultValues || !defaultValues.responsiblePersonId) return;
    handleResponsiblePersonChanged(userColors, defaultValues.responsiblePersonId);
  }, [userColors]);

  const handleResponsiblePersonChanged = (userColors: UserColor[], userId: number) => {
    const colorClass = getUserColorClass(userColors, userId);
    setUserColorClass(colorClass);
  };

  const handleSelectCustomer = useCallback(
    (_customerId: number) => {
      if (!formRef || !formRef.current) return;
      formRef.current.instance.updateData('topLevelAssetId', null);
      formRef.current.instance.updateData('assetId', null);
      formRef.current.instance.updateData('customerProjectId', null);
      setSelectedCustomerId(_customerId);
    },
    [formRef],
  );

  const projectsWithGroupName = projects?.map((project) => ({
    ...project,
    groupName: project.projectStatusIsActive
      ? translate!('Active projects', transFormName)
      : translate!('Inactive projects', transFormName),
  }));

  const onHtmlEditorContentReady = useCallback((e: any) => {
    const container = e.element.closest('.dx-htmleditor');
    if (container) {
      container.style.resize = 'vertical';
      container.style.overflow = 'auto';
      container.style.minHeight = '250px';
    }
  }, []);

  const onHtmlEditorFocusOut = useCallback(() => {
    if (!isDirty) setDirty();
  }, [isDirty, setDirty]);

  const htmlEditorOptions = useMemo(
    () => ({
      height: 250,
      resizable: { handles: 's' },
      toolbar: {
        items: [
          'undo',
          'redo',
          'bold',
          'italic',
          'underline',
          'strike',
          'alignLeft',
          'alignCenter',
          'alignRight',
          'alignJustify',
          'orderedList',
          'bulletList',
          'fontSize',
          'fontColor',
          'color',
          'background',
          'link',
          'clear',
        ],
      },
      mediaResizing: { enabled: true },
      onContentReady: onHtmlEditorContentReady,
      onFocusOut: onHtmlEditorFocusOut,
    }),
    [],
  );

  if (
    !customers ||
    !taskActionRespPersons ||
    !statuses ||
    !taskRatings ||
    (selectedCustomerId && !projects) ||
    !documentTypes ||
    !capacityCategories ||
    !topLevelAssets ||
    !assets ||
    !responsiblePersons ||
    !faultTypes ||
    !userColors
  )
    return <Loader />;

  return (
    <div className="task-service-edit-form">
      <form onSubmit={onSubmit}>
        <Form
          formData={formData.current}
          ref={formRef}
          colCount={4}
          onFieldDataChanged={(e) => {
            setDirty();
          }}
        >
          <GroupItem colCount={1} colSpan={3} cssClass={'left-column'}>
            <GroupItem
              colSpan={2}
              colCount={20}
              cssClass={`headline${!showTaskNameInput ? ' no-edit' : ''}`}
            >
              <SimpleItem
                dataField="taskName"
                editorType="dxTextBox"
                colSpan={18}
                editorOptions={{
                  disabled: !showTaskNameInput,
                }}
              >
                <Label text={`${task.customId}`} />
                <RequiredRule message={translate!('Name is required', transFormName)} />
              </SimpleItem>
              <GroupItem colSpan={2} colCount={2}>
                {statuses.filter((x) => x.actionName === 'Edit').length > 0 && (
                  <ButtonItem verticalAlignment={'center'} horizontalAlignment={'right'}>
                    <ButtonOptions
                      type={'default'}
                      stylingMode={'text'}
                      icon={showTaskNameInput ? 'back' : 'edit'}
                      useSubmitBehavior={false}
                      onClick={() => setShowTaskNameInput(!showTaskNameInput)}
                    />
                  </ButtonItem>
                )}
                <ButtonItem verticalAlignment={'center'} horizontalAlignment={'right'}>
                  <ButtonOptions
                    type={'default'}
                    stylingMode={'text'}
                    icon={'copy'}
                    useSubmitBehavior={false}
                    onClick={() => history.push(`/tasks/new/${defaultValues.id}`)}
                  />
                </ButtonItem>
              </GroupItem>
            </GroupItem>
            <GroupItem colSpan={2} cssClass={'card'}>
              <GroupItem>
                <div className={'html-editor-title'}>
                  <div>{translate!('Description', transFormName)}</div>
                  {statuses.filter((x) => x.actionName === 'Edit').length > 0 && (
                    <Button
                      type="default"
                      stylingMode="text"
                      icon={showHtmlEditor ? 'back' : 'edit'}
                      onClick={() => setShowHtmlEditor(!showHtmlEditor)}
                    />
                  )}
                </div>
              </GroupItem>
              <GroupItem visible={showHtmlEditor}>
                <SimpleItem
                  visible={showHtmlEditor}
                  dataField="description"
                  editorType="dxHtmlEditor"
                  colSpan={3}
                  editorOptions={htmlEditorOptions}
                >
                  <Label location={'top'} text={''} visible={false} />
                </SimpleItem>
              </GroupItem>
              <GroupItem visible={!showHtmlEditor}>
                <div className="iframe-wrapper">
                  <iframe sandbox="" srcDoc={formData.current.description} />
                </div>
              </GroupItem>
              <GroupItem>
                <SimpleItem
                  colSpan={3}
                  dataField="diagnose"
                  editorType="dxHtmlEditor"
                  editorOptions={htmlEditorOptions}
                >
                  <Label location={'top'} text={translate!('Diagnose', transFormName)} />
                </SimpleItem>
              </GroupItem>
              <GroupItem>
                <TabPanel
                  selectedIndex={currentTab}
                  dataSource={tabs}
                  itemRender={(a) => a.component}
                  activeStateEnabled={true}
                  onTitleClick={(e: any) => setCurrentTab(e.itemIndex)}
                  deferRendering={false}
                  focusStateEnabled={false}
                />
              </GroupItem>
            </GroupItem>
          </GroupItem>
          <GroupItem cssClass={'right-column'}>
            <GroupItem cssClass={'part-1'}>
              <GroupItem cssClass={'requestorGroup'}>
                <SimpleItem dataField="requestorName" editorType="dxTextBox">
                  <Label text={translate!('Requestor name', transFormName)} />
                </SimpleItem>
                <SimpleItem dataField="requestorEmail" editorType="dxTextBox">
                  <Label text={translate!('Requestor email', transFormName)} />
                  <CustomRule
                    message={translate!('Enter valid email', transFormName)}
                    validationCallback={(e) => {
                      return !e.value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.value);
                    }}
                  />
                </SimpleItem>
                <SimpleItem dataField="requestorPhone" editorType="dxTextBox">
                  <Label text={translate!('Requestor phone', transFormName)} />
                  <CustomRule
                    message={translate!('Enter valid phone number', transFormName)}
                    validationCallback={(e) => {
                      return (
                        !e.value ||
                        /^(\+?[0-9]{1,4}( ?))?[0-9]{3}( ?)[0-9]{3}( ?)[0-9]{3}$/.test(e.value)
                      );
                    }}
                  />
                </SimpleItem>
              </GroupItem>
              <SimpleItem
                dataField={'customerId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: customers,
                  onValueChanged: (e: any) => handleSelectCustomer(e.value),
                }}
              >
                <Label text={translate!('Customer', transFormName)} />
              </SimpleItem>
              <SimpleItem
                cssClass={`dropdown-user-color ${projectColorClass ? projectColorClass : ''}`}
                dataField={'customerProjectId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: new DataSource({
                    store: projectsWithGroupName,
                    group: {
                      selector: 'groupName',
                      desc: false,
                    },
                  }),
                  readOnly: !selectedCustomerId,
                  grouped: true,
                  groupExpr: 'groupName',
                  onValueChanged: (e: any) => {
                    handleCustomerProjectChanged(e.value);

                    if (!projects) return;
                    const color = projects.find((x) => x.id === e.value);
                    if (color) {
                      // @ts-ignore
                      setProjectColorClass(getColorClass(color.color));
                    }
                  },
                  itemTemplate: (data: any, _index: number, element: any) => {
                    if (!projects) return;
                    const color = projects.find((x) => x.id === data.id);
                    if (color)
                      element.innerHTML = `<div style="color: #${color.color}">${data.name}</div>`;
                    else element.innerHTML = `<div>${data.name}</div>`;
                  },
                }}
              >
                <Label text={translate!('Project', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField={'billingStatus'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: Object.values(CustomerProjectBillingStatus).map(
                    (status: CustomerProjectBillingStatus) => {
                      return {
                        id: status,
                        name: translate!(
                          CustomerProjectBillingStatusTranslation[status],
                          `CustomersProjects.Status`,
                        ),
                      };
                    },
                  ),
                }}
              >
                <Label text={translate!('Billing status', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField={'typeId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: documentTypes,
                  onValueChanged: handleDocumentTypeChanged,
                }}
              >
                <Label text={translate!('Type', transFormName)} />
                <RequiredRule message={translate!('Type is required', transFormName)} />
              </SimpleItem>
              {isShowDowntime && (
                <SimpleItem
                  dataField="downtimeFrom"
                  editorType="dxDateBox"
                  editorOptions={{
                    ...dateTimeFieldOptionsWithSerialization,
                  }}
                >
                  <Label text={translate!('Downtime from', transFormName)} />
                  <RequiredRule message={translate!('Downtime from required', transFormName)} />
                </SimpleItem>
              )}
              {isShowDowntime && (
                <SimpleItem
                  dataField="downtimeTo"
                  editorType="dxDateBox"
                  editorOptions={{
                    ...dateTimeFieldOptionsWithSerialization,
                  }}
                >
                  <Label text={translate!('Downtime to', transFormName)} />
                </SimpleItem>
              )}
              {isShowFaultType && (
                <SimpleItem
                  dataField={'faultTypeId'}
                  editorType={'dxSelectBox'}
                  editorOptions={{
                    ...dxSelectBoxConfig,
                    valueExpr: 'id',
                    displayExpr: 'name',
                    dataSource: faultTypes,
                  }}
                >
                  <Label text={translate!('Fault type', transFormName)} />
                </SimpleItem>
              )}
            </GroupItem>
            <GroupItem cssClass={'part-2'}>
              <SimpleItem
                cssClass={`dropdown-user-color ${userColorClass}`}
                dataField="responsiblePersonId"
                editorType="dxSelectBox"
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'personalNumber',
                  displayExpr: 'fullName',
                  items: responsiblePersons,
                  onValueChanged: (e) => handleResponsiblePersonChanged(userColors, e.value),
                  itemTemplate: (data: any, _index: number, element: any) => {
                    const color = userColors.find((x) => x.userId === data.personalNumber);
                    if (color)
                      element.innerHTML = `<div style="color: #${color.color}">${data.fullName}</div>`;
                    else element.innerHTML = `<div>${data.fullName}</div>`;
                  },
                }}
              >
                <Label text={translate!('Responsible person', transFormName)} />
              </SimpleItem>

              <SimpleItem
                dataField={'capacityCategoryId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: capacityCategories,
                }}
              >
                <Label text={translate!('Capacity category', transFormName)} />
                <RequiredRule
                  message={translate!('Capacity category is required', transFormName)}
                />
              </SimpleItem>
              <SimpleItem
                dataField="statusNameTranslated"
                editorType="dxTextBox"
                editorOptions={{
                  readOnly: true,
                }}
              >
                <Label text={translate!('Status', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField="requiredDate"
                editorType="dxDateBox"
                editorOptions={{
                  ...dateFieldOptionsWithSerialization,
                  disabledDates: (args) => {
                    const date = moment(formatDate(args.date, 'yyyy-MM-dd'));
                    const today = moment();
                    const _oldRequiredDate = moment(defaultValues.requiredDate, 'Y-MM-DD');
                    return date.isBefore(today) && !date.isSame(_oldRequiredDate);
                  },
                }}
              >
                <Label text={translate!('Required date', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField="creationTime"
                editorType="dxDateBox"
                editorOptions={{
                  ...dateTimeFieldOptions,
                }}
              >
                <Label text={translate!('Date of creation', transFormName)} />
                <RequiredRule message={translate!('Date of creation is required', transFormName)} />
              </SimpleItem>
            </GroupItem>

            <SimpleItem
              dataField={'topLevelAssetId'}
              editorType={'dxSelectBox'}
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'id',
                displayExpr: 'name',
                dataSource: selectedCustomerId
                  ? topLevelAssets?.filter((asset) =>
                      assetsAssignToCustomer?.some((assigned) => assigned.id === asset.id),
                    )
                  : topLevelAssets,
                onValueChanged: (e: any) => setSelectedTopLevelAssetId(e.value),
              }}
            >
              <Label text={translate!('Parent asset', transFormName)} />
            </SimpleItem>
            <SimpleItem
              dataField={'assetId'}
              editorType={'dxSelectBox'}
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'id',
                displayExpr: 'name',
                dataSource: selectedCustomerId
                  ? assets.filter((asset) =>
                      assetsAssignToCustomer?.some((assigned) => assigned.id === asset.id),
                    )
                  : assets,
                onValueChanged: (e: any) => handleAssetChanged(e.value),
              }}
            >
              <Label text={translate!('Asset', transFormName)} />
              <RequiredRule message={translate!('Asset is required', transFormName)} />
            </SimpleItem>
            <GroupItem cssClass={'total-elapsed-time'}>
              <div>Celkem vykázáno:</div>
              <div className="value">{timeSpanToHoursMinutes(worksheetsTotalElapsedTime)}</div>
            </GroupItem>
            <GroupItem cssClass={'buttons'}>
              <ButtonItem verticalAlignment={'center'} horizontalAlignment={'left'}>
                <ButtonOptions
                  text={translate!('Close', transFormName)}
                  type={'normal'}
                  useSubmitBehavior={false}
                  onClick={() => handleDirtyWithCallback(safeGoBack)}
                />
              </ButtonItem>
              {statuses.filter((x) => x.actionName === 'Edit').length > 0 && (
                <ButtonItem verticalAlignment={'center'} horizontalAlignment={'left'}>
                  <ButtonOptions
                    text={translate!('Save', transFormName)}
                    type={'success'}
                    useSubmitBehavior
                  />
                </ButtonItem>
              )}
            </GroupItem>
            <GroupItem cssClass={'actions-container'}>
              <TaskChangeStatus
                task={task}
                mutate={() => {
                  onActionDone();
                }}
                responsiblePersons={taskActionRespPersons}
                showCurrentStatus={false}
                statuses={statuses}
                mutateStatuses={mutateStatuses}
                poupWidth={600}
                isEditForm={true}
                oldRequiredDate={task.requiredDate}
                customValidationFunction={customValidationFunctionActions}
                taskRatings={taskRatings}
                isOnlyButtons
                noPadding
              />
            </GroupItem>
            <GroupItem>
              <RelatedAsset taskId={task.id} />
            </GroupItem>
          </GroupItem>
        </Form>
      </form>
      {prompt}
    </div>
  );
};
